import { COUNTRY } from '@qflow/constants'

export const isValidTimeZone = (tz: string | null): boolean => {
  try {
    if (!Intl || !Intl.DateTimeFormat().resolvedOptions().timeZone) {
      return false
    }

    if (typeof tz !== 'string') {
      return false
    }

    // throws an error if timezone is not valid
    Intl.DateTimeFormat(undefined, { timeZone: tz })
    return true
  } catch {
    return false
  }
}

export const usDateFormatter = (dateString: string): string => {
  if (!dateString || dateString === '') {
    return ''
  }
  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric'
  })
}

export const getLocaleFromCountry = (country: string | null): string => {
  // NOTE: Backend also has a similar mapping
  // See https://dev.azure.com/qualisflow/Platform/_git/services?path=/src/Qflow.Framework.Common/Internationalisation/Countries.cs&version=GBdev/main&line=42&lineEnd=43&lineStartColumn=1&lineEndColumn=1&lineStyle=plain&_a=contents
  if (typeof country === 'string') {
    switch (country) {
      case COUNTRY.AUSTRALIA:
        return 'en-AU'
      case COUNTRY.CANADA:
        return 'en-CA'
      case COUNTRY.FRANCE:
        return 'fr-FR'
      case COUNTRY.GERMANY:
        return 'de-DE'
      case COUNTRY.IRELAND:
        return 'en-IE'
      case COUNTRY.SWITZERLAND:
        return 'de-CH'
      case COUNTRY.UK:
        return 'en-GB'
      case COUNTRY.USA:
        return 'en-US'
      default:
        console.warn(`Unsupported Locale mapping for country: ${country}`)
    }
  } else {
    console.warn(
      `Invalid country value for Locale mapping: ${country}`,
      typeof country
    )
  }

  return 'en-GB'
}

/** Returns the number of minutes that need to be added to the date to alter it to UTC. */
export const getOffset = (timeZone: string, date: Date) => {
  if (!isValidTimeZone(timeZone)) {
    return 0
  }

  const timeZoneName = Intl.DateTimeFormat('ia', {
    timeZoneName: 'shortOffset',
    timeZone: timeZone || 'Europe/London'
  })
    ?.formatToParts(date)
    ?.find(i => i.type === 'timeZoneName')?.value

  // Remove "GMT" prefix
  const offset = timeZoneName?.slice(3)

  if (!offset) return 0

  const matchData = offset.match(/([+-])(\d{1,2})(?::(\d{1,2}))?/)
  if (!matchData) throw new Error(`Cannot parse timezone name: ${timeZoneName}`)

  const [, sign, hour, minute] = matchData
  let result = parseInt(hour) * 60

  if (minute) result += parseInt(minute)
  if (sign === '+') result *= -1

  return Object.is(result, -0) ? 0 : result
}

/** NOTE: Dates are treated as if local machine timezone, so even if `Z` UTC strings, they will have any local daylight savings applied. */
export const isFirstDateAfterSecondDateUsingOffsets = (
  firstDate: string,
  timeZone: string,
  secondDate: string
) => {
  if (firstDate && timeZone && secondDate) {
    // NOTE: Dates utilise local machine as the default timezone.
    const firstDateObj = new Date(firstDate)
    const secondDateObj = new Date(secondDate)

    // Remove the timezone offset from the first date
    const firstDateConvertedFromTimeZone = subtractTimeZoneOffsetFromDate(
      firstDateObj,
      timeZone
    )

    // new Date() will apply local machine offset/daylight savings, so we need to convert back to UTC for comparison
    const secondDateWithOffsetApplied =
      convertFromLocalMachineToUTC(secondDateObj)
    return firstDateConvertedFromTimeZone > secondDateWithOffsetApplied
  }
  return false
}

/** Removes Offset specified by the supplied TimeZone from the Date. This does not return UTC, but a date assuming the local machine TimeZone/Daylight Savings. */
export const subtractTimeZoneOffsetFromDate = (
  date: Date,
  timeZone: string
): Date => {
  return new Date(date.getTime() + getOffset(timeZone, date) * 60000)
}

/** Removes local machine offset/daylight savings to give a UTC value */
export const convertFromLocalMachineToUTC = (date: Date): Date => {
  return new Date(date.getTime() + date.getTimezoneOffset() * 60000)
}
